/**
 * NBC School Holidays API
 *
 * Two endpoints:
 *   - getStates(country): list of regions/states (Bundesländer) for a country
 *   - getSchoolHolidays(country, state, year?): school holidays for a state
 *
 * Without an explicit year the API returns the current year only, so
 * getSchoolHolidaysWithNextYear() additionally requests next year and merges.
 *
 * All responses are cached in-memory for the page session.
 *
 * @module services/api/nbc/getHolidays
 */

import { nbcHolidaysClient } from './nbcHolidaysClient';
import { NBC_HOLIDAYS_ENDPOINTS } from './config';
import {
  NBCStatesResponseSchema,
  NBCHolidaysResponseSchema,
  type NBCState,
  type NBCStatesResponse,
  type NBCHoliday,
  type NBCHolidaysResponse,
} from '@/types/schemas/nbc/holidays';

const statesCache = new Map<string, NBCState[]>();
const holidaysCache = new Map<string, NBCHoliday[]>();

export async function getStates(country: string): Promise<NBCState[]> {
  const cached = statesCache.get(country);
  if (cached) return cached;

  const response = await nbcHolidaysClient.get<NBCStatesResponse>({
    endpoint: NBC_HOLIDAYS_ENDPOINTS.STATES(country),
    responseSchema: NBCStatesResponseSchema,
  });

  statesCache.set(country, response.states);
  return response.states;
}

export async function getSchoolHolidays(
  country: string,
  state: string,
  year?: number
): Promise<NBCHoliday[]> {
  const cacheKey = `${country}/${state}/${year ?? 'default'}`;
  const cached = holidaysCache.get(cacheKey);
  if (cached) return cached;

  const response = await nbcHolidaysClient.get<NBCHolidaysResponse>({
    endpoint: NBC_HOLIDAYS_ENDPOINTS.SCHOOL_HOLIDAYS(country, state, year),
    responseSchema: NBCHolidaysResponseSchema,
  });

  holidaysCache.set(cacheKey, response);
  return response;
}

/**
 * Merge holiday lists, keeping the first occurrence of each name + period.
 *
 * The current-year and next-year responses overlap (e.g. Weihnachtsferien
 * 2026-12-24 → 2027-01-08 is returned by both), so periods are deduplicated
 * individually and holidays left without any period are dropped — `periods`
 * is `min(1)` in the schema.
 */
function mergeHolidays(lists: NBCHoliday[][]): NBCHoliday[] {
  const seen = new Set<string>();
  const merged: NBCHoliday[] = [];

  for (const list of lists) {
    for (const holiday of list) {
      const periods = holiday.periods.filter((period) => {
        const key = `${holiday.name}::${period.start}::${period.end}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (periods.length > 0) {
        merged.push({ ...holiday, periods });
      }
    }
  }

  return merged;
}

/**
 * School holidays for the current year plus the next one.
 *
 * A failing next-year request is non-fatal: the current year is returned on its
 * own so the selector stays usable. A failing current-year request rethrows.
 */
export async function getSchoolHolidaysWithNextYear(
  country: string,
  state: string
): Promise<NBCHoliday[]> {
  const nextYear = new Date().getFullYear() + 1;

  const [current, next] = await Promise.allSettled([
    getSchoolHolidays(country, state),
    getSchoolHolidays(country, state, nextYear),
  ]);

  if (current.status === 'rejected') {
    throw current.reason;
  }

  if (next.status === 'rejected') {
    console.warn(
      '[getSchoolHolidaysWithNextYear] Failed to load holidays for',
      nextYear,
      next.reason
    );
    return current.value;
  }

  return mergeHolidays([current.value, next.value]);
}
